
/**
 * Robosen Halloween Progress
 * Refreshes the progress indicator after cart changes.
 * Does not add gifts or modify cart contents.
 */

(() => {
  if (window.__mtHalloweenProgressLoaded) return;
  window.__mtHalloweenProgressLoaded = true;

  let refreshTimer = null;
  let refreshVersion = 0;
  let cartSignature = '';
  let refreshing = false;

  const selector = '[data-halloween-progress]';

  function getProgress() {
    return document.querySelector(selector);
  }

  function getCartDrawer() {
    return document.querySelector('#cart-drawer');
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshProgress, 350);
  }

  async function refreshProgress() {
    const current = getProgress();
    if (!current) return;

    const sectionId = current.dataset.sectionId;
    if (!sectionId) return;

    const version = ++refreshVersion;
    refreshing = true;

    try {
      const url = new URL('/cart', window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) throw new Error('Section refresh failed');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const next = doc.querySelector(selector);

      if (!next || version !== refreshVersion) return;

      const live = getProgress();
      if (!live) return;

      // Preserve the old bar for a smooth progress animation.
      const oldFill = live.querySelector(
        '.mt-halloween-progress__fill'
      );

      const newFill = next.querySelector(
        '.mt-halloween-progress__fill'
      );

      const oldWidth = oldFill?.style.width || '0%';
      const newWidth = newFill?.style.width || '0%';

      if (newFill) newFill.style.width = oldWidth;

      live.replaceWith(next);

      if (newFill && oldWidth !== newWidth) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            newFill.style.width = newWidth;
          });
        });
      }
    } catch (error) {
      console.warn('[Halloween Progress]', error);
    } finally {
      refreshing = false;
    }
  }

  async function checkCartChange() {
    if (refreshing || !getProgress()) return;

    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) return;

      const cart = await response.json();

      // Include prices, quantities and applied discounts.
      const signature = JSON.stringify({
        items: cart.items.map(item => ({
          key: item.key,
          quantity: item.quantity,
          final_line_price: item.final_line_price,
          discounts: item.discounts
        })),
        total: cart.total_price
      });

      if (cartSignature && signature !== cartSignature) {
        scheduleRefresh();
      }

      cartSignature = signature;
    } catch (error) {
      console.warn('[Halloween Cart Check]', error);
    }
  }

  // Common theme and cart update events.
  [
    'cart:change',
    'cart:refresh',
    'cart:updated',
    'cart:updated:success',
    'theme:cart:change'
  ].forEach(eventName => {
    document.addEventListener(eventName, scheduleRefresh);
  });

  // Monitor native cart line-item DOM changes.
  let lineItemsObserver = null;
  let observedLineItems = null;

  function observeLineItems() {
    const drawer = getCartDrawer();
    const lineItems = drawer?.querySelector(
      '.cart-drawer__line-items'
    );

    if (lineItems === observedLineItems) return;

    lineItemsObserver?.disconnect();
    observedLineItems = lineItems;

    if (!lineItems) return;

    lineItemsObserver = new MutationObserver(() => {
      scheduleRefresh();
    });

    lineItemsObserver.observe(lineItems, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  document.addEventListener('click', event => {
    if (
      event.target.closest(
        '#cart-drawer button, #cart-drawer [data-quantity]'
      )
    ) {
      setTimeout(checkCartChange, 500);
    }
  });

  document.addEventListener('change', event => {
    if (event.target.closest('#cart-drawer')) {
      setTimeout(checkCartChange, 500);
    }
  });

  // Observe drawer replacement by the theme.
  const rootObserver = new MutationObserver(() => {
    observeLineItems();
  });

  function initialize() {
    observeLineItems();

    rootObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    checkCartChange();

    // Fallback for AJAX updates by third-party apps/themes.
    // Check only when the cart drawer is visible.
    setInterval(() => {
      const drawer = getCartDrawer();

      if (!drawer || !getProgress()) return;

      const visible = drawer.getBoundingClientRect().width > 0 &&
        (drawer.hasAttribute('open') ||
         drawer.getAttribute('aria-hidden') !== 'true');

      if (visible && document.visibilityState === 'visible') {
        checkCartChange();
      }
    }, 2500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, {
      once: true
    });
  } else {
    initialize();
  }
})();
